import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Search, BookOpen, PlayCircle, FileQuestion, ChevronRight, Settings2, Clock, Home, Trophy, User } from "lucide-react";
import { PlanBadge } from "@/components/PlanBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readJson = (k) => { try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; } };

export default function MyCourses() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [semFilter, setSemFilter] = useState("all");
  const recent = (readJson("cm_recent_courses") || []).slice(0, 4);

  useEffect(() => {
    const init = async () => {
      let uniId = null, uniName = "", majorId = null, majorName = "";
      const token = localStorage.getItem("session_token");
      if (token) {
        try {
          const res = await fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
          if (res.ok) {
            const me = await res.json();
            uniId = me.university_id; majorId = me.major_id;
          }
        } catch { /* fall back to local */ }
      }
      const local = readJson("cm_onboarding") || {};
      uniId = uniId || local.university_id || readJson("cm_last_uni")?.id;
      majorId = majorId || local.major_id;
      uniName = local.university_name || readJson("cm_last_uni")?.name || "";
      majorName = local.major_name || "";
      if (!uniId) { navigate("/onboarding", { replace: true }); return; }
      if (!majorId) { navigate(`/onboarding?uni=${encodeURIComponent(uniId)}`, { replace: true }); return; }
      setPrefs({ uniId, uniName, majorId, majorName });
      try {
        const url = `${API}/courses/by-university/${uniId}${majorId ? `?major_id=${encodeURIComponent(majorId)}` : ""}`;
        const res = await fetch(url);
        const data = await res.json();
        setCourses(Array.isArray(data) ? data : []);
      } catch { setCourses([]); }
      setLoading(false);
    };
    init();
  }, [navigate]);

  const years = useMemo(() => [...new Set(courses.map((c) => c.year).filter(Boolean))].sort((a, b) => a - b), [courses]);
  const semesters = useMemo(() => [...new Set(courses.map((c) => c.semester).filter(Boolean))].sort((a, b) => a - b), [courses]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return courses.filter((c) =>
      (yearFilter === "all" || c.year === Number(yearFilter)) &&
      (semFilter === "all" || c.semester === Number(semFilter)) &&
      (!q || (c.course_name || "").toLowerCase().includes(q)));
  }, [courses, query, yearFilter, semFilter]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0B1524]" data-testid="my-courses-page">
      <header className="sticky top-0 z-40 bg-white/90 dark:bg-[#0B1524]/90 backdrop-blur border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="mycourses-logo">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-[#1E3A5F] to-[#0F2744]">
              <GraduationCap className="h-5 w-5 text-[#FF6B35]" />
            </div>
            <span className="text-xl font-extrabold text-[#1E3A5F] dark:text-white">Class<span className="text-[#2563EB] dark:text-[#7EB2FF]">Mate</span></span>
          </button>
          <button onClick={() => navigate("/onboarding")} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-300 hover:text-[#2563EB]" data-testid="change-university-btn">
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">Change University / Program</span>
            <span className="sm:hidden">Change</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-24 md:pb-10">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0F2744] dark:text-white" style={{ fontFamily: "'Libre Baskerville', serif" }} data-testid="my-courses-title">
          My Courses
        </h1>
        {prefs && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="my-courses-context">
            {[prefs.uniName || prefs.uniId, prefs.majorName].filter(Boolean).join(" · ")}
          </p>
        )}
        <PlanBadge />

        {/* Continue strip */}
        {recent.length > 0 && (
          <section className="mt-5" data-testid="recent-courses-strip">
            <h2 className="text-sm font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-2">
              <Clock className="h-4 w-4" /> Recently studied
            </h2>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {recent.map((c) => (
                <button key={c.id} onClick={() => navigate(`/course/${c.id}`)}
                        className="shrink-0 max-w-[220px] flex items-center gap-2 rounded-xl bg-[#2563EB]/5 dark:bg-[#2563EB]/15 border border-[#2563EB]/20 px-3 py-2 text-left hover:bg-[#2563EB]/10 transition-colors"
                        data-testid={`recent-course-${c.id}`}>
                  <PlayCircle className="h-4 w-4 text-[#2563EB] shrink-0" />
                  <span className="text-[13px] font-semibold text-[#0F2744] dark:text-white truncate">{c.name}</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Search + optional filters */}
        <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search your courses…"
                   className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-transparent text-[#0F2744] dark:text-white focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20 outline-none text-sm"
                   data-testid="course-search-input" />
          </div>
          {years.length > 1 && (
            <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-medium text-[#0F2744] dark:text-white"
                    data-testid="year-filter">
              <option value="all">All years</option>
              {years.map((y) => <option key={y} value={y}>Year {y}</option>)}
            </select>
          )}
          {semesters.length > 1 && (
            <select value={semFilter} onChange={(e) => setSemFilter(e.target.value)}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-medium text-[#0F2744] dark:text-white"
                    data-testid="semester-filter">
              <option value="all">All semesters</option>
              {semesters.map((s) => <option key={s} value={s}>Semester {s}</option>)}
            </select>
          )}
        </div>

        {/* Course grid */}
        {loading ? (
          <p className="text-center text-slate-400 py-14">Loading your courses…</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5" data-testid="my-courses-grid">
            {filtered.map((c) => (
              <button key={c.external_id} onClick={() => navigate(`/course/${c.external_id}`)}
                      className="group rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-700/80 p-4 text-left hover:border-[#2563EB] hover:shadow-lg hover:shadow-blue-600/5 transition-all"
                      data-testid={`course-card-${c.external_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#2563EB]/10 shrink-0">
                    <BookOpen className="h-5 w-5 text-[#2563EB]" />
                  </span>
                  {(c.year || c.semester) && (
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">
                      {[c.year ? `Y${c.year}` : null, c.semester ? `S${c.semester}` : null].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </div>
                <p className="mt-2.5 font-bold text-[#0F2744] dark:text-white leading-snug line-clamp-2">{c.course_name}</p>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500 dark:text-slate-400">
                  {c.mcq_count > 0 && <span className="flex items-center gap-1"><FileQuestion className="h-3 w-3" />{c.mcq_count} questions</span>}
                </div>
                <span className="mt-2.5 inline-flex items-center gap-0.5 text-[13px] font-bold text-[#2563EB] group-hover:gap-1.5 transition-all">
                  Start learning <ChevronRight className="h-4 w-4" />
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full text-center text-slate-400 py-14" data-testid="no-courses-msg">
                No courses match. Try clearing filters or <button onClick={() => navigate("/onboarding")} className="text-[#2563EB] font-semibold underline">change your program</button>.
              </p>
            )}
          </div>
        )}
      </main>

      {/* Mobile bottom nav — consistent with landing */}
      <nav className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white dark:bg-[#0B1524] border-t border-slate-200 dark:border-slate-700" data-testid="mycourses-bottom-nav">
        <div className="grid grid-cols-5 h-16">
          {[
            { icon: Home, label: "Home", to: "/", tid: "nav-home" },
            { icon: BookOpen, label: "My Courses", to: "/my-courses", tid: "nav-courses", active: true },
            { icon: FileQuestion, label: "Practice", to: recent[0]?.id ? `/course/${recent[0].id}` : "/my-courses", tid: "nav-practice" },
            { icon: Trophy, label: "Champion", to: "/championship", tid: "nav-champion" },
            { icon: User, label: "Profile", to: "/dashboard", tid: "nav-profile" },
          ].map((t) => (
            <button key={t.tid} onClick={() => navigate(t.to)} className="flex flex-col items-center justify-center gap-0.5" data-testid={t.tid}>
              <t.icon className={`h-5 w-5 ${t.active ? "text-[#2563EB]" : "text-slate-400"}`} />
              <span className={`text-[10px] font-medium ${t.active ? "text-[#2563EB]" : "text-slate-400"}`}>{t.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
